/**
 * ReportResultTable — renders a Custom Report Engine envelope
 * ({ columns, rows, summary, notices }). Shared by the authoring Preview and
 * (later) the Back Office run view. Indents hierarchy rows by _level and
 * header-detail rows by _group_level when present.
 */

import React from 'react';
import { Icons } from '../../common/Icons';

const fmt = (value, type) => {
  if (value === null || value === undefined) return '';
  if (type === 'currency') {
    const n = Number(value);
    return isNaN(n) ? String(value) : '$' + n.toFixed(2);
  }
  if (type === 'integer') {
    const n = parseInt(value, 10);
    return isNaN(n) ? String(value) : n.toLocaleString('en-US');
  }
  return String(value);
};

const footerTotals = (columns, rows) => {
  const totals = {};
  for (const col of columns) {
    if (col.type === 'currency' || col.type === 'integer' || col.type === 'number') {
      totals[col.key] = rows.reduce((s, r) => s + parseFloat(r[col.key] || 0), 0);
    }
  }
  return Object.keys(totals).length ? totals : null;
};

const ReportResultTable = ({ result }) => {
  if (!result) return null;
  const { columns = [], rows = [], summary = [], notices = [] } = result;

  // Control columns drive indentation but are not themselves rendered as cells.
  const indentKey = columns.some((c) => c.key === '_level')
    ? '_level'
    : (columns.some((c) => c.key === '_group_level') ? '_group_level' : null);
  const visibleColumns = columns.filter((c) => c.key !== '_level' && c.key !== '_group_level');
  const totals = footerTotals(visibleColumns, rows);

  return (
    <div>
      {notices.length > 0 && (
        <div className="alert alert-info mb-3">
          <Icons.Info size={18} />
          <div>
            {notices.map((n, i) => <div key={i}>{n}</div>)}
          </div>
        </div>
      )}

      {summary.length > 0 && (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {summary.map((s) => (
            <div key={s.key} className="card" style={{ padding: '12px 16px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{s.label}</div>
              <div style={{ fontSize: '20px', fontWeight: 600 }}>{fmt(s.value, s.type)}</div>
            </div>
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="empty-state">
          <Icons.FileText size={48} />
          <p>No rows returned.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>{visibleColumns.map((c) => <th key={c.key}>{c.label || c.key}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => {
                const depth = indentKey ? (Number(row[indentKey]) || 0) : 0;
                return (
                  <tr key={ri}>
                    {visibleColumns.map((c, ci) => (
                      <td key={c.key} style={ci === 0 && depth ? { paddingLeft: (depth * 20 + 12) + 'px' } : undefined}>
                        {fmt(row[c.key], c.type)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
            {totals && (
              <tfoot>
                <tr style={{ fontWeight: 600, borderTop: '2px solid var(--border-color)' }}>
                  {visibleColumns.map((c, ci) => {
                    if (ci === 0) {
                      return <td key={c.key}>Total</td>;
                    }
                    if (c.key === 'stall_fee_status') {
                      return <td key={c.key} />;
                    }
                    if (c.type === 'currency' || c.type === 'integer' || c.type === 'number') {
                      return <td key={c.key} style={{ textAlign: 'right' }}>{fmt(totals[c.key], c.type)}</td>;
                    }
                    return <td key={c.key} />;
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportResultTable;
